import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { publicCatalog } from '@/config/membershipCatalog';
import QualificationFormModal from '@/components/QualificationForm';
import { useSiteLang } from '@/components/site/SiteLang';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Target,
  ChevronDown,
  Zap,
  Trophy,
  Flame,
  Star,
  Sparkles,
  Award,
} from 'lucide-react';

// Equipo fundador (sección "Quién lo imparte"). Fotos provisionales recortadas de la pieza de equipo.
const FOUNDER_PHOTOS = {
  'Antonio Gimeno': '/team/antonio-gimeno.jpg',
  'Daniel Serrano': '/team/daniel-serrano.jpg',
  'Luna Trejos': '/team/luna-trejos.jpg',
};

const T = {
  es: {
    founders: [
      { name: 'Antonio Gimeno', role: 'CCO y Director Comercial', focus: 'Estrategia comercial, marketing y desarrollo de la red de brokers.' },
      { name: 'Daniel Serrano', role: 'COO', focus: 'Alianzas estratégicas, comunicación y construcción de comunidades.' },
      { name: 'Luna Trejos', role: 'Directora de Formación y Comunidad', focus: 'Academia, membresías y activación de la comunidad.' },
    ],
    days: [
      {
        day: 'Día 1',
        title: 'Cambio de paradigma',
        desc: 'Entiendes cómo la digitalización y la tokenización están transformando el sector inmobiliario y aprendes a explicar el modelo del club con claridad en 90 segundos.'
      },
      {
        day: 'Día 2',
        title: 'Marco jurídico y técnico',
        desc: 'Trabajas los marcos jurídicos y técnicos de la tokenización inmobiliaria y practicas cómo comunicarlos con casos reales del sector y feedback del equipo.'
      },
      {
        day: 'Día 3',
        title: 'El día que más recuerdan',
        desc: 'El día más intenso: simulaciones, análisis de casos reales y práctica en grupo con feedback directo del equipo fundador. Termina con un encuentro de networking de la cohorte que los anteriores participantes describen como el momento que lo cambia todo.'
      },
      {
        day: 'Día 4',
        title: 'Certificación y plan de 30 días',
        desc: 'Consolidas lo aprendido, obtienes tu certificación privada FHC como Broker Digital en Tokenización Inmobiliaria y sales con un plan de acción semana a semana para el primer mes.'
      },
    ],
    hero: {
      badge: 'Bootcamp',
      h1a: '4 días de inmersión práctica.',
      h1b: 'Sales certificado y con tu plan de acción de 30 días.',
      subA: 'El Bootcamp donde obtienes la ',
      subStrong: 'certificación privada FHC como Broker Digital en Tokenización Inmobiliaria.',
      included: 'Incluido en la membresía Premium. Plazas limitadas por edición.',
      tagline: 'No escuchas cómo se hace. Lo practicas.',
      secondaryCta: 'Ver programa completo',
      chips: ['4 días intensivos', 'Práctica con casos reales', 'Certificación privada FHC', 'Plan de acción de 30 días'],
    },
    problem: {
      eyebrow: 'El problema',
      h2a: 'Se habla mucho de tokenización inmobiliaria. Y casi nadie sabe ',
      h2b: 'explicarla.',
      p1: 'No porque el concepto sea difícil. Sino porque la mayoría de las formaciones se quedan en la teoría y nunca ponen a prueba lo aprendido.',
      p2: 'Conocen el concepto. Entienden la tecnología. Pero cuando llega el momento de explicárselo con claridad a un cliente o a un colega, algo falla.',
      teachA: 'La formación tradicional ',
      teachB: 'enseña.',
      trainA: 'Este bootcamp ',
      trainB: 'entrena.',
      diff: 'Y hay una diferencia enorme entre las dos cosas.',
      imgAlt: 'Villa de la Costa Blanca que ha acogido ediciones del bootcamp',
    },
    happens: {
      eyebrow: 'Lo que ocurre en 4 días',
      h2a: 'No vas a escuchar presentaciones sobre cómo se hace. ',
      h2b: 'Vas a practicarlo.',
      p1: 'Desde el primer día entiendes el cambio que vive el sector y aprendes a explicarlo con criterio. El segundo día trabajas el marco jurídico y técnico y lo pones en práctica con casos reales del sector y el feedback del equipo.',
      p2a: 'El tercero es el día que todos recuerdan — simulaciones, práctica en grupo y un encuentro de networking de la cohorte que los participantes anteriores describen como ',
      p2Strong: 'el momento que lo cambia todo.',
      p3: 'El cuarto día consolidas, obtienes tu certificación privada FHC y sales con un plan de acción para los próximos 30 días.',
      boxA: 'Cuatro días. ',
      boxB: 'Un plan de acción claro.',
    },
    program: {
      eyebrow: 'El programa',
      h2: '4 días de transformación',
      goalStrong: 'Objetivo concreto:',
      goal: ' salir con la certificación privada FHC, criterio propio y tu plan de acción de 30 días.',
    },
    location: {
      altAerial: 'Vista aérea de Villa Amaroo, en la Costa Blanca',
      altPool: 'Zona exterior de Villa Amaroo',
      altVenue: 'Villa Amaroo, donde se han celebrado ediciones anteriores del bootcamp',
      eyebrow: 'Dónde se celebra',
      h2: 'No entrenas en una sala de hotel.',
      h3: 'Entrenas en un entorno privado, cara a cara con el equipo.',
      lead: 'Encuentros presenciales en la Costa Blanca.',
      p1: 'Ediciones anteriores del bootcamp se han celebrado en Villa Amaroo, cerca de Busot (Alicante). El lugar de cada edición se confirma al reservar tu plaza.',
      p2: 'Durante cuatro días entrenas en un entorno privado, analizas casos reales y practicas con el equipo. Cuando tengas que explicarle a alguien cómo la tecnología está cambiando el sector inmobiliario, no lo habrás leído en un PDF. Lo habrás practicado.',
      italic: 'Eso cambia la forma en que lo explicas.',
      list: [
        '4 días de inmersión práctica',
        'Networking con el equipo fundador y otros miembros',
        'Entorno privado sin distracciones',
        'Casos reales del sector inmobiliario',
      ],
    },
    testimonials: {
      eyebrow: 'Testimonios',
      h2: 'Lo que dicen los miembros de Freedom Homes Club',
      items: [
        {
          title: 'Ser socio de FHC es entrar en algo profesional, seguro y con mucho futuro',
          quote: 'Lo que más me sorprendió fue la profesionalidad. Hay una estructura, un respaldo legal y personas que conocen el tema al detalle. La tokenización inmobiliaria ya está aquí, y quienes entiendan esto a tiempo van a estar mejor posicionados.',
          name: 'Manoli Espinosa',
          role: 'Miembro FHC',
        },
      ],
    },
    leads: {
      eyebrow: 'Quién lo imparte',
      h2: 'El equipo fundador de Freedom Homes Club',
      p: 'El bootcamp lo imparte el equipo fundador del club: profesionales que enseñan desde la práctica real del sector inmobiliario y de la tecnología blockchain, no desde la teoría.',
      guests: 'Con invitados en cada edición: profesionales del sector inmobiliario, legal y tecnológico seleccionados según el perfil del grupo.',
      cohort: 'Cada cohorte es distinta. El método es el mismo. Lo que ocurre en la sala depende de quién está en ella.',
    },
    profile: {
      eyebrow: 'Para quién es',
      h2: 'Estás en el lugar correcto si:',
      items: [
        { title: 'Vendedores y emprendedores', desc: 'Que quieren entender a fondo el sector inmobiliario digital — con un método práctico y una certificación privada que acredita su formación.' },
        { title: 'Agentes inmobiliarios y brokers', desc: 'Que ven cómo el mercado evoluciona y quieren ser de los primeros en saber explicar lo que la mayoría todavía no entiende.' },
        { title: 'Miembros Premium FHC', desc: 'Que ya tienen la membresía Premium de Freedom Homes Club y quieren completar su formación presencial y obtener la certificación privada FHC.' },
      ],
      notForStrong: 'No es para personas que buscan inspiración.',
      notFor: ' Es para personas que quieren practicar y salir con un plan de acción concreto.',
      limited: 'Plazas limitadas. Selección por perfil.',
    },
    places: {
      eyebrow: 'Plazas',
      h2: 'El Bootcamp está incluido en la membresía Premium',
      p: 'El primer paso es el test de idoneidad. Después eliges día y hora para hablar con el equipo: te contamos las próximas ediciones y si la membresía Premium encaja contigo. Si ya eres miembro, las fechas y la reserva están en tu área privada.',
      memberLink: 'Ya soy miembro: reservar mi plaza',
    },
  },
  en: {
    founders: [
      { name: 'Antonio Gimeno', role: 'CCO and Sales Director', focus: 'Sales strategy, marketing and growing the broker network.' },
      { name: 'Daniel Serrano', role: 'COO', focus: 'Strategic partnerships, communications and community building.' },
      { name: 'Luna Trejos', role: 'Director of Training and Community', focus: 'Academy, memberships and community engagement.' },
    ],
    days: [
      {
        day: 'Day 1',
        title: 'A shift in perspective',
        desc: 'You see how digitalisation and tokenisation are transforming the property sector, and learn to explain the club’s model clearly in 90 seconds.'
      },
      {
        day: 'Day 2',
        title: 'The legal and technical framework',
        desc: 'You work through the legal and technical frameworks behind property tokenisation and practise explaining them, using real cases from the sector and feedback from the team.'
      },
      {
        day: 'Day 3',
        title: 'The day people remember most',
        desc: 'The most intense day: simulations, real case studies and group practice with direct feedback from the founding team. It closes with a cohort networking session that past participants describe as the moment that changes everything.'
      },
      {
        day: 'Day 4',
        title: 'Certification and your 30-day plan',
        desc: 'You consolidate what you’ve learnt, earn your private FHC certification as a Digital Broker in Property Tokenisation and leave with a week-by-week action plan for your first month.'
      },
    ],
    hero: {
      badge: 'Bootcamp',
      h1a: 'Four days of hands-on practice.',
      h1b: 'Leave certified, with your 30-day action plan.',
      subA: 'The Bootcamp where you earn the ',
      subStrong: 'private FHC certification as a Digital Broker in Property Tokenisation.',
      included: 'Included in Premium membership. Limited places per edition.',
      tagline: 'You don’t just hear how it’s done. You do it.',
      secondaryCta: 'See the full programme',
      chips: ['Four intensive days', 'Practice with real cases', 'Private FHC certification', '30-day action plan'],
    },
    problem: {
      eyebrow: 'The problem',
      h2a: 'Everyone’s talking about property tokenisation. Almost no one can ',
      h2b: 'explain it.',
      p1: 'Not because the idea is difficult. Because most training stops at theory and never puts what you’ve learnt to the test.',
      p2: 'People know the concept. They understand the technology. But when it’s time to explain it clearly to a client or a colleague, something doesn’t land.',
      teachA: 'Traditional training ',
      teachB: 'teaches.',
      trainA: 'This bootcamp ',
      trainB: 'trains.',
      diff: 'And the difference between the two is enormous.',
      imgAlt: 'Costa Blanca villa that has hosted previous editions of the bootcamp',
    },
    happens: {
      eyebrow: 'What happens in four days',
      h2a: 'You won’t sit through presentations on how it’s done. ',
      h2b: 'You’ll practise it.',
      p1: 'From day one, you understand the shift the sector is going through and learn to explain it with sound judgement. On day two, you work through the legal and technical framework and put it into practice with real cases from the sector and feedback from the team.',
      p2a: 'Day three is the one everyone remembers — simulations, group practice and a cohort networking session that past participants describe as ',
      p2Strong: 'the moment that changes everything.',
      p3: 'On day four, you bring it all together, earn your private FHC certification and leave with an action plan for the next 30 days.',
      boxA: 'Four days. ',
      boxB: 'One clear action plan.',
    },
    program: {
      eyebrow: 'The programme',
      h2: 'Four days that change how you work',
      goalStrong: 'The goal:',
      goal: ' to leave with the private FHC certification, a point of view of your own and your 30-day action plan.',
    },
    location: {
      altAerial: 'Aerial view of Villa Amaroo, on the Costa Blanca',
      altPool: 'Outdoor area at Villa Amaroo',
      altVenue: 'Villa Amaroo, where previous editions of the bootcamp have been held',
      eyebrow: 'Where it takes place',
      h2: 'You don’t train in a hotel conference room.',
      h3: 'You train in a private setting, face to face with the team.',
      lead: 'In-person sessions on the Costa Blanca.',
      p1: 'Previous editions of the bootcamp have been held at Villa Amaroo, near Busot (Alicante). The venue for each edition is confirmed when you book your place.',
      p2: 'Over four days, you train in a private setting, work through real cases and practise with the team. When you need to explain how technology is changing the property sector, you won’t have read it in a PDF. You’ll have practised it.',
      italic: 'That changes the way you explain it.',
      list: [
        'Four days of hands-on practice',
        'Networking with the founding team and other members',
        'A private setting, free from distractions',
        'Real cases from the property sector',
      ],
    },
    testimonials: {
      eyebrow: 'Testimonials',
      h2: 'What Freedom Homes Club members say',
      items: [
        {
          title: 'Becoming an FHC member means joining something professional, secure and with a real future',
          quote: 'What surprised me most was the professionalism. There’s a structure, legal backing and people who know the subject inside out. Property tokenisation is already here, and those who understand it early will be better placed.',
          name: 'Manoli Espinosa',
          role: 'FHC member',
        },
      ],
    },
    leads: {
      eyebrow: 'Who leads it',
      h2: 'The founding team of Freedom Homes Club',
      p: 'The bootcamp is led by the club’s founding team: professionals who teach from first-hand experience in property and blockchain technology, not from theory.',
      guests: 'Each edition also features guests: property, legal and technology professionals chosen to suit the group.',
      cohort: 'Every cohort is different. The method stays the same. What happens in the room depends on who’s in it.',
    },
    profile: {
      eyebrow: 'Who it’s for',
      h2: 'You’re in the right place if you’re one of these:',
      items: [
        { title: 'Salespeople and entrepreneurs', desc: 'Who want a thorough understanding of the digital property sector — with a practical method and a private certification that recognises their training.' },
        { title: 'Estate agents and brokers', desc: 'Who can see the market changing and want to be among the first able to explain what most people still don’t understand.' },
        { title: 'FHC Premium members', desc: 'Who already hold Freedom Homes Club Premium membership and want to complete their in-person training and earn the private FHC certification.' },
      ],
      notForStrong: 'It’s not for people looking for inspiration.',
      notFor: ' It’s for people who want to practise and leave with a concrete action plan.',
      limited: 'Limited places. Selection by profile.',
    },
    places: {
      eyebrow: 'Places',
      h2: 'The Bootcamp is included in Premium membership',
      p: 'The first step is the suitability test. Then you choose a day and time to speak with the team: we’ll tell you about upcoming editions and whether Premium membership is right for you. If you’re already a member, dates and booking are in your members’ area.',
      memberLink: 'Already a member? Book my place',
    },
  },
};

export default function BootcampTokenizacion() {
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const { bootcampNote: BOOTCAMP_NOTE, testCta: TEST_CTA } = publicCatalog(lang);
  const FOUNDERS = t.founders.map((f) => ({ ...f, photo: FOUNDER_PHOTOS[f.name] }));

  // La inscripción no es pública: el bootcamp va incluido en Premium y la plaza se reserva desde el
  // panel de miembro. Regla de Samuel (10/10/2026): sin precios y CTA único al test de idoneidad;
  // tras el test, el lead agenda su llamada.
  const [testOpen, setTestOpen] = useState(false);
  const [testSource, setTestSource] = useState('bootcamp');
  const openTest = (src = 'bootcamp') => { setTestSource(src); setTestOpen(true); };

  const scrollToProgram = () => {
    document.getElementById('programa')?.scrollIntoView({ behavior: 'smooth' });
  };

  const days = t.days;

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0">

      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center"
        data-testid="hero-section"
      >
        {/* Foto real (resolución media): siempre bajo degradado azul noche */}
        <img src="/media/amaroo-interior.jpg" alt="" fetchPriority="high" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060d1b]/85 via-[#0a1628]/75 to-[#060d1b]/95" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
          <div className="inline-flex items-center gap-2 border border-[#D4AF37]/50 bg-[#D4AF37]/10 backdrop-blur-sm px-5 py-2.5 mb-8 animate-fade-in-up">
            <Flame className="w-4 h-4 text-[#D4AF37]" />
            <span className="font-body text-xs uppercase tracking-widest text-[#D4AF37]">
              {t.hero.badge}
            </span>
          </div>

          <h1 className="font-heading text-[2.1rem] sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in-up delay-100" data-testid="hero-headline">
            {t.hero.h1a}<br />
            <span className="italic text-[#D4AF37]">{t.hero.h1b}</span>
          </h1>

          <p className="font-body text-base sm:text-lg md:text-xl text-white/80 max-w-3xl mx-auto mb-4 leading-relaxed animate-fade-in-up delay-200">
            {t.hero.subA}<strong className="text-white">{t.hero.subStrong}</strong>
          </p>
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 mb-4 animate-fade-in-up delay-200">
            <span className="font-body text-sm text-[#D4AF37]">{t.hero.included}</span>
          </div>
          <p className="font-body text-base text-white/60 max-w-2xl mx-auto mb-10 animate-fade-in-up delay-200">
            {t.hero.tagline}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-fade-in-up delay-300">
            <Button
              onClick={() => openTest('bootcamp-hero')}
              className="h-14 px-8 bg-[#D4AF37] text-[#0F172A] hover:bg-[#C5A028] font-bold tracking-wide rounded-none inline-flex items-center justify-center gap-2 animate-pulse-gold"
              data-testid="hero-cta-primary"
            >
              {TEST_CTA}
              <ArrowRight className="w-5 h-5" />
            </Button>
            <Button
              onClick={scrollToProgram}
              variant="outline"
              className="h-14 px-8 border-white/50 text-white hover:bg-white/10 font-medium tracking-wide rounded-none backdrop-blur-sm"
              data-testid="hero-cta-secondary"
            >
              {t.hero.secondaryCta}
            </Button>
          </div>

          <div className="flex flex-wrap justify-center gap-2 sm:gap-4 animate-fade-in-up delay-400">
            {t.hero.chips.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2">
                <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-body text-sm text-white">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce hidden sm:block">
          <ChevronDown className="w-8 h-8 text-white/50" />
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="problem-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">
                {t.problem.eyebrow}
              </span>
              <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-6 leading-tight">
                {t.problem.h2a}<span className="italic text-[#D4AF37]">{t.problem.h2b}</span>
              </h2>
              <p className="font-body text-base text-slate-600 mb-4 leading-relaxed">
                {t.problem.p1}
              </p>
              <p className="font-body text-base text-slate-600 mb-4 leading-relaxed">
                {t.problem.p2}
              </p>
              <div className="p-6 bg-[#0F172A] mt-6">
                <p className="font-heading text-xl text-white">
                  {t.problem.teachA}<span className="italic text-white/60">{t.problem.teachB}</span>
                </p>
                <p className="font-heading text-xl text-[#D4AF37] mt-2">
                  {t.problem.trainA}<span className="italic">{t.problem.trainB}</span>
                </p>
                <p className="font-body text-sm text-white/50 mt-3">
                  {t.problem.diff}
                </p>
              </div>
            </div>

            <div className="relative h-[320px] md:h-[450px] overflow-hidden">
              <img
                src="/media/amaroo-vista.jpg"
                alt={t.problem.imgAlt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/60 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* What Happens in 4 Days */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="what-happens-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">
              {t.happens.eyebrow}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-4">
              {t.happens.h2a}<span className="italic text-[#D4AF37]">{t.happens.h2b}</span>
            </h2>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto" />
          </div>

          <div className="max-w-3xl mx-auto">
            <p className="font-body text-lg text-slate-600 leading-relaxed mb-6">
              {t.happens.p1}
            </p>
            <p className="font-body text-lg text-slate-600 leading-relaxed mb-6">
              {t.happens.p2a}<strong className="text-[#0F172A]">{t.happens.p2Strong}</strong>
            </p>
            <p className="font-body text-lg text-slate-600 leading-relaxed mb-8">
              {t.happens.p3}
            </p>
            <div className="bg-[#0F172A] p-6 text-center">
              <p className="font-heading text-2xl text-white">{t.happens.boxA}<span className="text-[#D4AF37]">{t.happens.boxB}</span></p>
            </div>
          </div>
        </div>
      </section>

      {/* Program Section */}
      <section id="programa" className="py-24 md:py-32 relative bg-[#0F172A]" data-testid="program-section">

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">
              {t.program.eyebrow}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
              {t.program.h2}
            </h2>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {days.map((day, idx) => (
              <div
                key={idx}
                className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 hover:bg-white/10 transition-colors"
                data-testid={`day-${idx + 1}`}
              >
                <div className="text-[#D4AF37] font-body text-xs uppercase tracking-widest mb-3">
                  {day.day}
                </div>
                <h3 className="font-heading text-xl text-white mb-4">{day.title}</h3>
                <p className="font-body text-sm text-white/70 leading-relaxed">{day.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 bg-[#D4AF37]/20 border border-[#D4AF37]/50 px-4 sm:px-6 py-4 text-left">
              <Trophy className="w-6 h-6 text-[#D4AF37] flex-shrink-0" />
              <p className="font-body text-white">
                <strong className="text-[#D4AF37]">{t.program.goalStrong}</strong>{t.program.goal}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="location-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <img
                src="/media/amaroo-aerea.jpg"
                alt={t.location.altAerial}
                loading="lazy"
                className="w-full h-44 sm:h-56 object-cover"
              />
              <img
                src="/media/amaroo-piscina.jpg"
                alt={t.location.altPool}
                loading="lazy"
                className="w-full h-44 sm:h-56 object-cover"
              />
              <img
                src="/media/amaroo-sede.jpg"
                alt={t.location.altVenue}
                loading="lazy"
                className="col-span-2 w-full h-48 sm:h-56 object-cover"
              />
            </div>

            <div>
              <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">
                {t.location.eyebrow}
              </span>
              <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-2 leading-tight">
                {t.location.h2}
              </h2>
              <h3 className="font-heading text-2xl text-[#D4AF37] italic mb-6">
                {t.location.h3}
              </h3>
              <p className="font-body text-lg text-[#0F172A] font-semibold mb-2">
                {t.location.lead}
              </p>
              <p className="font-body text-base text-slate-600 mb-4 leading-relaxed">
                {t.location.p1}
              </p>
              <p className="font-body text-base text-slate-600 mb-6 leading-relaxed">
                {t.location.p2}
              </p>
              <p className="font-body text-sm text-[#0F172A] italic mb-6">
                {t.location.italic}
              </p>
              <div className="space-y-3 mb-8">
                {t.location.list.map((item, idx) => (
                  <p key={idx} className="font-body text-slate-700 flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    {item}
                  </p>
                ))}
              </div>
              <p className="font-body text-xs text-slate-500 mb-6">{BOOTCAMP_NOTE}</p>
              <Button
                onClick={() => openTest('bootcamp-ubicacion')}
                className="h-12 px-6 bg-[#0F172A] text-white hover:bg-[#1E293B] font-semibold rounded-none inline-flex items-center gap-2"
                data-testid="location-cta"
              >
                {TEST_CTA}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">
              {t.testimonials.eyebrow}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-4">
              {t.testimonials.h2}
            </h2>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto" />
          </div>

          {(() => {
            const testimonials = t.testimonials.items;
            const TestimonialCard = ({ t, idx }) => (
              <div className="bg-white p-8 border border-slate-100 hover:border-[#D4AF37]/30 transition-colors flex flex-col" data-testid={`testimonial-${idx}`}>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />)}
                </div>
                <p className="font-heading text-sm text-[#0F172A] mb-3 leading-snug">"{t.title}"</p>
                <p className="font-body text-slate-500 text-sm leading-relaxed mb-6 italic flex-1">{t.quote}</p>
                <div className="border-t border-slate-100 pt-4">
                  <p className="font-heading text-sm text-[#0F172A]">{t.name}</p>
                  <p className="font-body text-xs text-slate-400">{t.role}</p>
                </div>
              </div>
            );
            // Con un solo testimonio, tarjeta centrada en lugar de una rejilla de tres columnas medio vacía
            if (testimonials.length === 1) {
              return <div className="max-w-2xl mx-auto"><TestimonialCard t={testimonials[0]} idx={0} /></div>;
            }
            return (
              <div className="space-y-6">
                <div className="grid md:grid-cols-3 gap-6">
                  {testimonials.slice(0, 3).map((t, idx) => <TestimonialCard key={idx} t={t} idx={idx} />)}
                </div>
                <div className="grid md:grid-cols-2 gap-6 md:max-w-[calc(66.666%+12px)] md:mx-auto">
                  {testimonials.slice(3).map((t, idx) => <TestimonialCard key={idx + 3} t={t} idx={idx + 3} />)}
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* Who Leads Section */}
      <section className="py-24 md:py-32 bg-[#0F172A]" data-testid="who-leads-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">
            {t.leads.eyebrow}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
            {t.leads.h2}
          </h2>
          <p className="font-body text-lg text-white/70 max-w-3xl mx-auto leading-relaxed mb-12">
            {t.leads.p}
          </p>
          <div className="mx-auto mb-12 flex max-w-4xl flex-wrap justify-center gap-6 text-left" data-testid="founders-grid">
            {FOUNDERS.map((f) => (
              <figure key={f.name} className="group w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]">
                <div className="aspect-square overflow-hidden rounded-2xl border border-[#D4AF37]/25 bg-[#0a1628]">
                  <img
                    src={f.photo}
                    alt={`${f.name}, ${f.role}`}
                    loading="lazy"
                    width="420"
                    height="420"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </div>
                <figcaption className="mt-4">
                  <p className="font-heading text-lg text-white leading-tight">{f.name}</p>
                  <p className="font-body text-xs uppercase tracking-widest text-[#D4AF37] mt-1">{f.role}</p>
                  <p className="font-body text-sm text-white/60 mt-2 leading-relaxed">{f.focus}</p>
                </figcaption>
              </figure>
            ))}
          </div>
          <p className="font-body text-base text-white/60 max-w-2xl mx-auto leading-relaxed mb-8">
            {t.leads.guests}
          </p>
          <div className="inline-flex items-center gap-3 bg-[#D4AF37]/15 border border-[#D4AF37]/40 px-4 sm:px-6 py-4 text-left">
            <Sparkles className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
            <p className="font-body text-white/80 text-sm">
              {t.leads.cohort}
            </p>
          </div>
        </div>
      </section>

      {/* Profile Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="profile-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">
              {t.profile.eyebrow}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-4">
              {t.profile.h2}
            </h2>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto" />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[Zap, Target, Award].map((icon, idx) => ({ icon, ...t.profile.items[idx] })).map((item, idx) => (
              <div key={idx} className="text-center p-6 border border-slate-100 hover:border-[#D4AF37]/50 transition-colors">
                <div className="w-16 h-16 mx-auto bg-[#F8FAFC] flex items-center justify-center mb-4">
                  <item.icon className="w-8 h-8 text-[#0F172A]" />
                </div>
                <h3 className="font-heading text-lg text-[#0F172A] mb-2">{item.title}</h3>
                <p className="font-body text-sm text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center p-8 bg-[#0F172A]">
            <p className="font-body text-white/80">
              <strong className="text-[#D4AF37]">{t.profile.notForStrong}</strong>{t.profile.notFor}
              <span className="block mt-2 text-white/60">{t.profile.limited}</span>
            </p>
          </div>
        </div>
      </section>

      {/* Inscripción: solo desde el panel de miembro */}
      <section id="inscripcion" className="py-16 bg-[#0F172A] border-t border-white/[0.06]" data-testid="bootcamp-members-only">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-3 block">{t.places.eyebrow}</span>
          <h2 className="font-heading text-2xl md:text-3xl text-white mb-4">{t.places.h2}</h2>
          <p className="font-body text-white/70 mb-3">{t.places.p}</p>
          <p className="font-body text-sm text-white/50 mb-8">{BOOTCAMP_NOTE}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button type="button" onClick={() => openTest('bootcamp-plazas')} className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F172A] font-bold px-6 h-12" data-testid="plazas-cta">{TEST_CTA} <ArrowRight className="w-4 h-4" /></button>
            <Link to="/dashboard/eventos" className="inline-flex items-center justify-center border border-white/25 text-white px-6 h-12">{t.places.memberLink}</Link>
          </div>
        </div>
      </section>


      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white/95 backdrop-blur-sm border-t border-slate-200 shadow-lg md:hidden">
        <Button
          onClick={() => openTest('bootcamp-sticky')}
          className="w-full h-12 bg-[#D4AF37] text-[#0F172A] hover:bg-[#C5A028] font-bold rounded-none inline-flex items-center justify-center gap-2"
          data-testid="sticky-cta"
        >
          {TEST_CTA} <ArrowRight className="w-4 h-4" />
        </Button>
      </div>

      <QualificationFormModal isOpen={testOpen} onClose={() => setTestOpen(false)} source={testSource} lang={lang} />
    </div>
  );
}
